import { useState } from 'react';
import { Link } from 'react-router-dom';
import { aiCreators } from '../../../mocks/aiCreators';

const AICreatorsSection = () => {
  const [showAll, setShowAll] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const displayedCreators = showAll ? aiCreators : aiCreators.slice(0, 8);
  const verifiedCount = aiCreators.filter((c) => c.verified).length;

  return (
    <section className="mb-12">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl shadow-lg shadow-orange-500/20">
              <i className="ri-robot-2-fill text-white text-xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">AI Creators</h2>
            <span className="px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
              {verifiedCount} Verified
            </span>
          </div>
          <p className="text-gray-500 text-sm ml-[52px]">
            Discover talented AI filmmakers and visual storytellers from our potential, vibrant, inspirational community members
          </p>
        </div>
      </div>

      {/* Creators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {displayedCreators.map((creator) => (
          <Link
            key={creator.id}
            to={`/creators/${creator.slug}`}
            className="group relative bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg hover:border-orange-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
            onMouseEnter={() => setHoveredId(creator.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            {/* Cover */}
            <div className="relative h-28 overflow-hidden">
              <img
                src={creator.coverImage}
                alt={`${creator.name} cover`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

              {/* Category Badge */}
              <div className="absolute top-2.5 left-2.5">
                <span className="px-2 py-0.5 bg-black/50 backdrop-blur-sm text-white text-xs font-medium rounded-md">
                  {creator.category}
                </span>
              </div>

              {/* Verified Badge */}
              {creator.verified && (
                <div className="absolute top-2.5 right-2.5">
                  <span className="px-2 py-0.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-semibold rounded-md flex items-center gap-1">
                    <i className="ri-verified-badge-fill text-[10px]"></i>
                    Approved
                  </span>
                </div>
              )}
            </div>

            {/* Avatar */}
            <div className="relative px-4 -mt-10 z-10">
              <div className="relative inline-block">
                <img
                  src={creator.avatar}
                  alt={creator.name}
                  className="w-[72px] h-[72px] rounded-full object-cover border-[3px] border-white shadow-md"
                />
                {creator.verified && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 bg-gradient-to-br from-orange-500 to-rose-500 rounded-full flex items-center justify-center border-2 border-white">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="px-4 pt-1.5 pb-4">
              <h3 className="font-bold text-gray-900 text-base group-hover:text-orange-600 transition-colors leading-tight">
                {creator.name}
              </h3>
              <p className="text-xs text-orange-600 font-medium mt-0.5">{creator.role}</p>
              <p className="text-xs text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">{creator.bio}</p>

              {/* Stats Row */}
              <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-follow-line text-orange-500"></i>
                  {creator.followers}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-film-line text-orange-500"></i>
                  {creator.projects} projects
                </span>
              </div>

              {/* Featured Work */}
              <div className="mt-3 pt-2.5 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  <span className="font-medium text-gray-600">Featured:</span>{' '}
                  {creator.featuredWork}
                </p>
              </div>

              {/* Tools */}
              <div className="flex flex-wrap gap-1 mt-2.5">
                {creator.tools.slice(0, 3).map((tool, index) => (
                  <span
                    key={index}
                    className="px-1.5 py-0.5 bg-gray-50 text-gray-500 text-[10px] rounded border border-gray-100"
                  >
                    {tool}
                  </span>
                ))}
                {creator.tools.length > 3 && (
                  <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 text-[10px] rounded border border-gray-100">
                    +{creator.tools.length - 3}
                  </span>
                )}
              </div>
            </div>

            {/* Hover overlay link */}
            {hoveredId === creator.id && (
              <div className="absolute bottom-0 left-0 right-0 p-4 pt-8 bg-gradient-to-t from-white via-white/95 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="block w-full py-2 text-center text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-rose-500 rounded-lg shadow-md whitespace-nowrap">
                  View Profile
                </span>
              </div>
            )}
          </Link>
        ))}
      </div>

      {/* Show More / Less */}
      {aiCreators.length > 8 && (
        <div className="text-center mt-8">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer whitespace-nowrap"
          >
            {showAll ? (
              <>
                Show Less
                <i className="ri-arrow-up-s-line"></i>
              </>
            ) : (
              <>
                Show All {aiCreators.length} AI Creators
                <i className="ri-arrow-down-s-line"></i>
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};

export default AICreatorsSection;
